import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Hash } from "lucide-react";

import EventsSelectorMenu from "./EventsSelectorMenu";
import { getFixedPanelPositionFromRect } from "./eventSelectorGeometry";
import axiosInstance from "shared/services/api/axiosInstance";
import Icon from "shared/components/Icon";

const pickerItemClass =
  "block w-full truncate px-2.5 py-1.5 text-left text-[11px] transition-colors rounded-sm";
const pickerMenuClass =
  "absolute left-0 top-full z-[120] mt-0.5 inline-block min-w-[8rem] max-w-[min(100vw-2rem,16rem)] overflow-hidden rounded-lg border py-1 shadow-lg";

/** Retention start/return event may be a string (legacy / event kind) or
 *  an object `{ entityName, entityKind, spanKind }` for agent/span. This
 *  helper coerces either shape into the display name used for the trigger
 *  button label. */
function eventDisplayName(value) {
  if (value == null) return '';
  if (typeof value === 'object') return String(value.entityName || value.eventName || value.event || '');
  return String(value);
}

const RULE_OPTIONS = [
  { value: "on", label: "On" },
  { value: "on_or_after", label: "On or after" },
  { value: "on_or_before", label: "On or before" },
];

const INTERVAL_OPTIONS = [
  { value: "day", label: "Each day" },
  { value: "week", label: "Each week" },
  { value: "month", label: "Each month" },
];

const METRIC_OPTIONS = [
  { value: "retention_rate", label: "Retention rate (%)" },
  { value: "unique_users", label: "Unique users (#)" },
];

/** Max lag index for line/metric "Group by" field (matches chart horizon expectations). */
const GROUP_BY_MAX = { day: 59, week: 9, month: 2 };

function getGroupByMax(unit) {
  return GROUP_BY_MAX[unit] ?? GROUP_BY_MAX.day;
}

/**
 * Query config for Retention: start event and return event selection.
 * UI aligned with FunnelQueryConfig / FlowQueryConfig (compact rows + inline pickers).
 */
export default function RetentionQueryConfig({
  startEvent = "",
  onStartEventChange,
  returnEvent = "",
  onReturnEventChange,
  interval = "day",
  onIntervalChange,
  retentionRule = "on_or_after",
  onRetentionRuleChange,
  metric = "retention_rate",
  onMetricChange,
  countSubject = "user",
  onCountSubjectChange = null,
  accumulationGroupKey = null,
  onAccumulationGroupKeyChange = null,
  chartType = "retention-curve",
  retentionIntervals: _retentionIntervals = [],
  retentionGroupIndex = 0,
  onRetentionGroupIndexChange,
  availableEvents = [],
  teamId = null,
}) {
  /** { which: 'start' | 'return', top, left, width } */
  const [eventPicker, setEventPicker] = useState(null);
  const eventPickerMenuRef = useRef(null);
  const startEventRowRef = useRef(null);
  const returnEventRowRef = useRef(null);

  const [retentionMenuOpen, setRetentionMenuOpen] = useState(null); // 'rule' | 'interval' | 'metric' | 'subject' | null
  const retentionControlsRef = useRef(null);

  const [groupKeyOptions, setGroupKeyOptions] = useState([]);

  useEffect(() => {
    const raw =
      teamId != null && teamId !== ""
        ? typeof teamId === "object" && teamId !== null
          ? teamId._id ?? teamId.id
          : teamId
        : null;
    if (raw == null || raw === "") {
      setGroupKeyOptions([]);
      return undefined;
    }
    let cancelled = false;
    axiosInstance
      .get("/group-identities/keys", { params: { team_id: raw } })
      .then((res) => {
        if (cancelled || !res.data?.success) return;
        setGroupKeyOptions(Array.isArray(res.data.keys) ? res.data.keys : []);
      })
      .catch(() => {
        if (!cancelled) setGroupKeyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const countSubjects = useMemo(() => {
    const base = [{ id: "user", label: "User", groupKey: null }];
    const fromApi = (groupKeyOptions || []).map((k) => ({
      id: `group:${k.group_key}`,
      label: k.group_key,
      groupKey: k.group_key,
    }));
    return [...base, ...fromApi];
  }, [groupKeyOptions]);

  const subjectDisplayLabel =
    countSubject === "group" && accumulationGroupKey
      ? `Group · ${accumulationGroupKey}`
      : "User";

  const isSubjectRowActive = (s) => {
    if (s.groupKey) {
      return countSubject === "group" && accumulationGroupKey === s.groupKey;
    }
    return (countSubject || "user") === "user";
  };

  const handleCountSubjectPick = (row) => {
    if (!onCountSubjectChange) return;
    if (row.groupKey) {
      onCountSubjectChange("group");
      onAccumulationGroupKeyChange?.(row.groupKey);
    } else {
      onCountSubjectChange("user");
      onAccumulationGroupKeyChange?.(null);
    }
    setRetentionMenuOpen(null);
  };

  const eventOnlyOptions = (Array.isArray(availableEvents) ? availableEvents : []).filter(
    (evt) => (evt?.source || (evt?.type === "transaction" ? "transaction" : "event")) !== "transaction"
  );

  useEffect(() => {
    if (!eventPicker) return;
    const handleClickOutside = (e) => {
      if (eventPickerMenuRef.current?.contains(e.target)) return;
      if (startEventRowRef.current?.contains(e.target)) return;
      if (returnEventRowRef.current?.contains(e.target)) return;
      setEventPicker(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [eventPicker]);

  useEffect(() => {
    if (!retentionMenuOpen) return;
    const close = (e) => {
      if (retentionControlsRef.current?.contains(e.target)) return;
      setRetentionMenuOpen(null);
    };
    const esc = (e) => {
      if (e.key === "Escape") setRetentionMenuOpen(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [retentionMenuOpen]);

  const handleStartEventSelect = (name, kind = 'event', opts = {}) => {
    // Emit object form when entityKind agent/span; string for plain events.
    if (kind === 'agent' || kind === 'span') {
      onStartEventChange({ entityName: name || "", entityKind: kind, spanKind: opts.spanKind || null });
    } else {
      onStartEventChange(name || "");
    }
    setEventPicker(null);
  };

  const handleReturnEventSelect = (name, kind = 'event', opts = {}) => {
    if (kind === 'agent' || kind === 'span') {
      onReturnEventChange({ entityName: name || "", entityKind: kind, spanKind: opts.spanKind || null });
    } else {
      onReturnEventChange(name || "");
    }
    setEventPicker(null);
  };

  const handleClearStartEvent = () => {
    onStartEventChange("");
    setEventPicker(null);
  };

  const handleClearReturnEvent = () => {
    onReturnEventChange("");
    setEventPicker(null);
  };

  const ruleLabel =
    RULE_OPTIONS.find((o) => o.value === retentionRule)?.label || "On or after";
  const intervalLabel =
    INTERVAL_OPTIONS.find((o) => o.value === interval)?.label || "Each day";
  const metricLabel =
    METRIC_OPTIONS.find((o) => o.value === metric)?.label || "Retention rate (%)";

  const groupByMax = getGroupByMax(interval);
  const rawGroupIdx = Math.floor(Number(retentionGroupIndex));
  const groupByIndex = Math.min(
    Math.max(0, Number.isFinite(rawGroupIdx) ? rawGroupIdx : 0),
    groupByMax
  );

  const periodPrefix =
    interval === "week" ? "Week" : interval === "month" ? "Month" : "Day";

  useEffect(() => {
    const max = getGroupByMax(interval);
    const current = Number(retentionGroupIndex);
    const base = Number.isFinite(current) ? Math.floor(current) : 0;
    const v = Math.min(Math.max(0, base), max);
    if (v !== base) {
      onRetentionGroupIndexChange?.(v);
    }
  }, [interval, retentionGroupIndex, onRetentionGroupIndexChange]);

  return (
    <>
      {eventPicker &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998] bg-[color:var(--surface-overlay)]"
              aria-hidden
              onClick={() => setEventPicker(null)}
            />
            <div
              ref={eventPickerMenuRef}
              className="fixed z-[9999] flex max-h-[min(65vh,calc(100vh-24px))] flex-col overflow-hidden"
              style={{
                top: eventPicker.top,
                left: eventPicker.left,
                width: eventPicker.width,
                maxHeight: eventPicker.maxHeightPx ?? 480,
              }}
            >
              <EventsSelectorMenu
                availableEvents={eventOnlyOptions}
                onSelect={(name, kind, opts) =>
                  eventPicker.which === "start"
                    ? handleStartEventSelect(name, kind, opts)
                    : handleReturnEventSelect(name, kind, opts)
                }
                onClose={() => setEventPicker(null)}
                maxHeight={eventPicker.maxHeightPx ?? 480}
                showSourceTabs
              />
            </div>
          </>,
          document.body
        )}

      <div className="mb-2">
        <span className="text-sm font-medium text-ink-muted">Events</span>
      </div>

      <div
        className="mb-4 space-y-1 rounded-lg border p-2"
        style={{ borderColor: "var(--border-default)", backgroundColor: "var(--surface-base)" }}
      >
        <div className="space-y-1">
          <div
            ref={startEventRowRef}
            className="group/steprow relative flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs transition-colors hover:bg-surface-inverse/[0.04]"
          >
            <div
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-dotted text-[11px] font-semibold tabular-nums"
              style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
              aria-hidden
            >
              1
            </div>
            <span
              className="min-w-0 flex-1 cursor-pointer truncate text-[12px] font-medium leading-snug"
              style={{ color: "var(--text-secondary)" }}
              onClick={(e) => {
                if (eventPicker?.which === "start") setEventPicker(null);
                else {
                  setEventPicker({
                    which: "start",
                    ...getFixedPanelPositionFromRect(e.currentTarget.getBoundingClientRect()),
                  });
                }
              }}
              title={eventDisplayName(startEvent) ? "Change event" : "Select event"}
            >
              {eventDisplayName(startEvent) || "Select event"}
            </span>
            <span className="shrink-0 text-[10px] font-medium" style={{ color: "var(--text-muted)" }}>
              then
            </span>
            {eventDisplayName(startEvent) && (
              <div className="flex shrink-0 items-center gap-0 opacity-0 transition-opacity group-hover/steprow:opacity-100 group-focus-within/steprow:opacity-100">
                <button
                  type="button"
                  onClick={handleClearStartEvent}
                  className="rounded p-0.5 hover:opacity-70"
                  title="Remove event"
                >
                  <Icon name="delete" size={14} color="var(--text-muted)" />
                </button>
              </div>
            )}
          </div>

          <div
            ref={returnEventRowRef}
            className="group/steprow relative flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs transition-colors hover:bg-surface-inverse/[0.04]"
          >
            <div
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-dotted text-[11px] font-semibold tabular-nums"
              style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
              aria-hidden
            >
              2
            </div>
            <span
              className="min-w-0 flex-1 cursor-pointer truncate text-[12px] font-medium leading-snug"
              style={{ color: "var(--text-secondary)" }}
              onClick={(e) => {
                if (eventPicker?.which === "return") setEventPicker(null);
                else {
                  setEventPicker({
                    which: "return",
                    ...getFixedPanelPositionFromRect(e.currentTarget.getBoundingClientRect()),
                  });
                }
              }}
              title={eventDisplayName(returnEvent) ? "Change event" : "Select event"}
            >
              {eventDisplayName(returnEvent) || "Select event"}
            </span>
            {eventDisplayName(returnEvent) && (
              <div className="flex shrink-0 items-center gap-0 opacity-0 transition-opacity group-hover/steprow:opacity-100 group-focus-within/steprow:opacity-100">
                <button
                  type="button"
                  onClick={handleClearReturnEvent}
                  className="rounded p-0.5 hover:opacity-70"
                  title="Remove event"
                >
                  <Icon name="delete" size={14} color="var(--text-muted)" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div ref={retentionControlsRef} className="space-y-3">
        <p
          className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12px] leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>Count return</span>
            <span className="relative inline-flex items-center">
            <button
              type="button"
              aria-expanded={retentionMenuOpen === "rule"}
              aria-haspopup="listbox"
              aria-label="Retention match"
              onClick={() => setRetentionMenuOpen((m) => (m === "rule" ? null : "rule"))}
              className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug hover:opacity-80"
              style={{ fontSize: "12px", color: "var(--text-primary)" }}
            >
              {ruleLabel}
              <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
            </button>
            {retentionMenuOpen === "rule" && (
              <div
                role="listbox"
                className={`${pickerMenuClass} shadow-token-lg`}
                style={{
                  backgroundColor: "var(--surface-base)",
                  borderColor: "var(--border-default)",
                }}
              >
                {RULE_OPTIONS.map((opt) => {
                  const active = opt.value === retentionRule;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onRetentionRuleChange?.(opt.value);
                        setRetentionMenuOpen(null);
                      }}
                      className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                      style={{
                        color: active ? "var(--text-primary)" : "var(--text-secondary)",
                        backgroundColor: "transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </span>
            <span
              className="hidden h-3 w-px shrink-0 self-center opacity-40 sm:inline-block"
              style={{ backgroundColor: "var(--border-default)" }}
              aria-hidden
            />
            <span className="relative inline-flex items-center">
            <button
              type="button"
              aria-expanded={retentionMenuOpen === "interval"}
              aria-haspopup="listbox"
              aria-label="Accumulation interval"
              onClick={() => setRetentionMenuOpen((m) => (m === "interval" ? null : "interval"))}
              className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug hover:opacity-80"
              style={{ fontSize: "12px", color: "var(--text-primary)" }}
            >
              {intervalLabel}
              <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
            </button>
            {retentionMenuOpen === "interval" && (
              <div
                role="listbox"
                className={`${pickerMenuClass} shadow-token-lg`}
                style={{
                  backgroundColor: "var(--surface-base)",
                  borderColor: "var(--border-default)",
                }}
              >
                {INTERVAL_OPTIONS.map((opt) => {
                  const active = opt.value === interval;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onIntervalChange?.(opt.value);
                        setRetentionMenuOpen(null);
                      }}
                      className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                      style={{
                        color: active ? "var(--text-primary)" : "var(--text-secondary)",
                        backgroundColor: "transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </span>
          </span>
        </p>

        {(chartType === "line" || chartType === "metric") && (
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-dotted pt-2"
            style={{ borderColor: "var(--border-default)" }}
          >
            <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-[11px] font-medium text-ink">Group by</span>
              <div className="inline-flex items-center gap-1.5">
                <span className="text-[11px] font-medium" style={{ color: "var(--text-secondary)" }}>
                  {periodPrefix}
                </span>
                <input
                  type="number"
                  min={0}
                  max={groupByMax}
                  step={1}
                  value={groupByIndex}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") return;
                    const n = parseInt(raw, 10);
                    if (!Number.isFinite(n)) return;
                    onRetentionGroupIndexChange?.(Math.min(Math.max(0, n), groupByMax));
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") onRetentionGroupIndexChange?.(0);
                  }}
                  aria-label={`${periodPrefix} lag index (0–${groupByMax})`}
                  className="h-7 w-12 rounded border px-1 text-center text-[11px] font-semibold tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--trodo-primary)] focus-visible:ring-offset-1"
                  style={{
                    borderColor: "var(--border-default)",
                    color: "var(--text-primary)",
                    backgroundColor: "var(--surface-base)",
                  }}
                />
                {interval === "day" && groupByIndex === 0 && (
                  <span className="text-[10px]" style={{ color: "var(--text-muted)" }} title="Same as &lt;1 Day column">
                    &lt;1 day
                  </span>
                )}
                {interval === "week" && groupByIndex === 0 && (
                  <span className="text-[10px]" style={{ color: "var(--text-muted)" }} title="Same as &lt;1 Week column">
                    &lt;1 week
                  </span>
                )}
                {interval === "month" && groupByIndex === 0 && (
                  <span className="text-[10px]" style={{ color: "var(--text-muted)" }} title="Same as &lt;1 Month column">
                    &lt;1 month
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-dotted pt-2"
          style={{ borderColor: "var(--border-default)" }}
        >
          <div className="inline-flex items-center gap-1.5">
            <Hash className="h-3 w-3 shrink-0 text-ink" strokeWidth={2.25} aria-hidden />
            <div className="relative">
              <button
                type="button"
                aria-expanded={retentionMenuOpen === "subject"}
                aria-haspopup="listbox"
                aria-label="Retention grain"
                onClick={() => setRetentionMenuOpen((m) => (m === "subject" ? null : "subject"))}
                className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
                style={{ fontSize: "11px" }}
              >
                {subjectDisplayLabel}
                <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
              </button>
              {retentionMenuOpen === "subject" && (
                <div
                  role="listbox"
                  className={`${pickerMenuClass} shadow-token-lg`}
                  style={{
                    backgroundColor: "var(--surface-base)",
                    borderColor: "var(--border-default)",
                  }}
                >
                  {countSubjects.map((s) => {
                    const active = isSubjectRowActive(s);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        title={s.label}
                        onClick={() => handleCountSubjectPick(s)}
                        className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                        style={{
                          color: active ? "var(--text-primary)" : "var(--text-secondary)",
                          backgroundColor: "transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <span
            className="hidden h-3 w-px shrink-0 self-center opacity-40 sm:inline-block"
            style={{ backgroundColor: "var(--border-default)" }}
            aria-hidden
          />
          <div className="relative">
            <button
              type="button"
              aria-expanded={retentionMenuOpen === "metric"}
              aria-haspopup="listbox"
              aria-label="Retention metric"
              onClick={() => setRetentionMenuOpen((m) => (m === "metric" ? null : "metric"))}
              className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
              style={{ fontSize: "11px" }}
            >
              {metricLabel}
              <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
            </button>
            {retentionMenuOpen === "metric" && (
              <div
                role="listbox"
                className={`${pickerMenuClass} shadow-token-lg`}
                style={{
                  backgroundColor: "var(--surface-base)",
                  borderColor: "var(--border-default)",
                }}
              >
                {METRIC_OPTIONS.map((opt) => {
                  const active = opt.value === metric;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onMetricChange?.(opt.value);
                        setRetentionMenuOpen(null);
                      }}
                      className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                      style={{
                        color: active ? "var(--text-primary)" : "var(--text-secondary)",
                        backgroundColor: "transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
